import { THEMES } from '@galley/shared'
import { useT } from '../../i18n'
import { cx } from '../cx'
import styles from './Logo.module.css'
import { WORDMARK_PATH, WORDMARK_VIEWBOX } from './wordmarkPath'

/** Brand tomato: the misregistration layer is part of the logo, so it ignores the theme. */
const TOMATO = THEMES.galley.day.accent

export interface LogoProps {
  /** px. At 20px and below the mark drops to two wisps and one text line (06 §6.2). */
  size?: number
  /** Adds the tomato page fill, offset like a riso print. Off = pure currentColor. */
  spot?: boolean
  className?: string
}

/** The Steam Page mark: a dog-eared page with steam rising — hot off the press, fresh from the oven. */
export function Logo({ size = 24, spot = false, className }: LogoProps) {
  const small = size <= 20
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {spot && <path d="M7 11h9l3 3v8H7z" fill={TOMATO} stroke="none" data-misreg="" />}
      <path d="M6 10h9l3 3v8H6z" />
      <path d="M15 10v3h3" />
      {small ? (
        <>
          <path d="M9 16.5h6" />
          <path d="M10 7c-1-1.2 1-2 0-3.5M14 7c-1-1.2 1-2 0-3.5" />
        </>
      ) : (
        <>
          <path d="M9 16h6M9 18.5h4" />
          <path d="M8.5 7c-1-1.2 1-2 0-3.5M12 7c-1-1.2 1-2 0-3.5M15.5 7c-1-1.2 1-2 0-3.5" />
        </>
      )}
    </svg>
  )
}

export type WordmarkSize = 'sm' | 'md' | 'lg' | 'hero'

export interface WordmarkProps {
  size?: WordmarkSize
  /** Adds the "where students cook" lockup under the wordmark. */
  tagline?: boolean
  /**
   * When true the wordmark is purely visual (e.g. a link that already has an accessible
   * name). Otherwise it reads as "Galley".
   */
  decorative?: boolean
  className?: string
}

/**
 * Lowercase "galley" in Fraunces SOFT, outlined to SVG. From lg (≥48px tall) up, a tomato
 * layer sits a couple of pixels down and right of the ink: the riso misregistration.
 */
export function Wordmark({
  size = 'md',
  tagline = false,
  decorative = false,
  className,
}: WordmarkProps) {
  const t = useT()
  const misreg = size === 'lg' || size === 'hero'
  return (
    <span className={cx(styles.lockup, styles[size], className)}>
      <span className={styles.mark}>
        {misreg && (
          <svg
            className={styles.misreg}
            viewBox={WORDMARK_VIEWBOX}
            aria-hidden="true"
            focusable="false"
            data-misreg=""
          >
            <path d={WORDMARK_PATH} fill={TOMATO} />
          </svg>
        )}
        <svg className={styles.ink} viewBox={WORDMARK_VIEWBOX} aria-hidden="true" focusable="false">
          <path d={WORDMARK_PATH} fill="currentColor" />
        </svg>
        {!decorative && (
          <span className="visually-hidden" translate="no">
            {t.common.brand.name}
          </span>
        )}
      </span>
      {tagline && <span className={styles.tagline}>{t.common.brand.tagline}</span>}
    </span>
  )
}
